import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Grid, Card, CardContent, Typography, Button, IconButton, Tooltip,
  CircularProgress, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField,
} from "@mui/material";
import {
  Save, Refresh, Visibility, VisibilityOff, CheckCircle, Error,
  Warning, Settings, Security, Link as LinkIcon, Send, Email as EmailIcon,
  RestartAlt,
} from "@mui/icons-material";
import { hubApi } from "../services/hubApi";

const SECTIONS = {
  central_status: { label: "Status deste HUB Central", icon: "🟢", color: "#25D366",
    help: "Quando inativo, este Central responde 503 e HUBs Locais conectados sao forcados a usar fallback." },
  mode: { label: "Modo Operacional", icon: "🎯", color: "#6B48FF",
    help: "Define como este HUB opera: conectado ao Central OtimizAPP ou independente com Apps próprios." },
  central: { label: "Conexao com HUB Central", icon: "🔗", color: "#25D366",
    help: "Conecte este HUB ao servidor central OtimizAPP Connect. O administrador central fornece a URL e o token de acesso.",
    visibleWhen: "centralized" },
  meta: { label: "Meta (WhatsApp / Facebook / Instagram)", icon: "📱", color: "#1877F2",
    help: "Configurar seu proprio App Meta para Embedded Signup (WhatsApp, Facebook, Instagram).",
    actionLabel: "Abrir Meta Developers",
    actionUrl: "https://developers.facebook.com/apps/",
    visibleWhen: "independent",
    steps: [
      "1. Acesse developers.facebook.com e crie/selecione seu App",
      "2. Copie o App ID e App Secret",
      "3. Em WhatsApp → Configuração, copie o Config ID",
      "4. Cole nos campos abaixo e salve",
    ] },
  telegram: { label: "Telegram", icon: "✈️", color: "#0088CC",
    help: "Registrar em: my.telegram.org/apps (1 por HUB, serve para todos os canais).",
    actionLabel: "Abrir my.telegram.org",
    actionUrl: "https://my.telegram.org/apps",
    visibleWhen: "independent",
    steps: [
      "1. Acesse my.telegram.org/apps com seu número",
      "2. Crie um novo App (qualquer nome/plataforma)",
      "3. Copie o App api_id e App api_hash",
      "4. Cole nos campos abaixo e salve",
    ] },
  marketplace: { label: "Marketplace", icon: "🛒", color: "#25D366",
    help: "Configurar seu proprio portal de assinaturas de canais.",
    visibleWhen: "independent" },
  email_sistema: { label: "E-mail de sistema", icon: "✉️", color: "#6B48FF",
    help: "SMTP para o HUB enviar e-mails proprios — usado pelo 'Esqueci minha senha'. Ex.: Gmail/Workspace com senha de app.",
    steps: [
      "1. Servidor SMTP (ex: smtp.gmail.com) e porta (587 STARTTLS / 465 SSL)",
      "2. Usuario e senha (no Gmail/Workspace, use uma 'senha de app')",
      "3. Remetente (From) — pode ser o proprio usuario",
      "4. Salve e clique em Testar (envia um e-mail de teste para voce).",
    ] },
  security: { label: "Seguranca", icon: "🔒", color: "#6B48FF",
    help: "Chaves de criptografia, assinatura de webhooks e autenticacao." },
  services: { label: "Servicos", icon: "⚙️", color: "#FF6B00",
    help: "Redis, status dos servicos e reinicio." },
};

// Tradução dos valores de select para exibição
const OPTION_LABELS = {
  centralized: "Centralizado (OtimizAPP Connect)",
  independent: "Independente (Apps próprios)",
  full: "Completo (todos os canais)",
  "true": "Ativo",
  "false": "Inativo (responde 503 para Locais)",
  meta_only: "Apenas Meta (WA, FB, IG)",
  disabled: "Desativado",
  permissive: "Permissivo (nome antigo de Diagnostico)",
  diagnostic: "Diagnostico (confere e registra, nao recusa)",
  strict: "Restrito (recusa assinatura ausente ou invalida)",
};

const ORIGEM_DO_MODO = {
  "environment override": "variavel de ambiente do servidor (vence o painel)",
  painel: "painel",
  padrao_seguro: "padrao seguro (nao configurado)",
  padrao_desenvolvimento: "padrao de desenvolvimento (nao configurado)",
};

const ALL_CHANNELS = [
  { id: "whatsapp", label: "WhatsApp", ready: true },
  { id: "instagram", label: "Instagram", ready: true },
  { id: "facebook", label: "Facebook", ready: true },
  { id: "telegram", label: "Telegram", ready: true },
  { id: "email", label: "Email", ready: true },
  { id: "voip", label: "VoIP", ready: true },
  { id: "sip", label: "SIP Trunk", ready: true },
  { id: "tiktok", label: "TikTok", ready: false },
  { id: "sms", label: "SMS", ready: false },
  { id: "google_business", label: "Google Business", ready: false },
  { id: "mercadolivre", label: "Mercado Livre", ready: false },
  { id: "teams", label: "MS Teams", ready: false },
];

function SettingField({ field, onSave, onModeChange }) {
  const inputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [edited, setEdited] = useState(false);
  const [channelValues, setChannelValues] = useState(
    field.type === "channels" && field.valueMasked
      ? (typeof field.valueMasked === "string" ? field.valueMasked.split(",").filter(Boolean) : field.valueMasked)
      : ALL_CHANNELS.map((c) => c.id) // Default: todos habilitados
  );

  const handleSave = async () => {
    let value;
    if (field.type === "channels") {
      value = channelValues.join(",");
    } else {
      value = inputRef.current?.value;
    }
    if (!value && field.isSecret && field.configured) return;
    setSaving(true);
    try {
      await onSave(field.key, value);
      setEdited(false);
    } catch {}
    setSaving(false);
  };

  const toggleChannel = (chId) => {
    setChannelValues((prev) => {
      const next = prev.includes(chId) ? prev.filter((c) => c !== chId) : [...prev, chId];
      setEdited(true);
      return next;
    });
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
        <Typography sx={{ fontSize: 13, color: "#D1D5DB", fontWeight: 500 }}>{field.label}</Typography>
        {field.isSecret && (
          <Chip size="small" label="criptografado" sx={{ height: 14, fontSize: 8, bgcolor: "rgba(107,72,255,0.1)", color: "#6B48FF" }} />
        )}
        {field.requiresRestart && (
          <Chip size="small" label="requer reinicio" sx={{ height: 14, fontSize: 8, bgcolor: "rgba(245,158,11,0.1)", color: "#F59E0B" }} />
        )}
        {!field.requiresRestart && !field.isSecret && field.type !== "channels" && (
          <Chip size="small" label="aplica na hora" sx={{ height: 14, fontSize: 8, bgcolor: "rgba(37,211,102,0.1)", color: "#25D366" }} />
        )}
      </Box>

      {field.description && (
        <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 0.5, lineHeight: 1.4 }}>{field.description}</Typography>
      )}

      {field.efetivo && (
        <Typography sx={{ fontSize: 12, mb: 0.5, lineHeight: 1.4, color: field.efetivo.erro ? "#DC2626" : "#374151" }}>
          Em uso agora: <b>{OPTION_LABELS[field.efetivo.modo] || field.efetivo.modo}</b> (origem: {ORIGEM_DO_MODO[field.efetivo.origem] || field.efetivo.origem})
          {field.pendingRestart ? " - o valor salvo vale apos reiniciar" : ""}
          {field.efetivo.erro ? ` - ${field.efetivo.erro}` : field.efetivo.aviso ? ` - ${field.efetivo.aviso}` : ""}
        </Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          {field.type === "channels" ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {ALL_CHANNELS.map((ch) => {
                const active = channelValues.includes(ch.id);
                const canToggle = ch.ready;
                return (
                  <Chip
                    key={ch.id}
                    size="small"
                    label={ch.ready ? ch.label : `${ch.label} (em breve)`}
                    onClick={canToggle ? () => toggleChannel(ch.id) : undefined}
                    sx={{
                      fontSize: 11, fontWeight: 500,
                      cursor: canToggle ? "pointer" : "default",
                      opacity: ch.ready ? 1 : 0.45,
                      bgcolor: !ch.ready ? "rgba(255,255,255,0.02)"
                        : active ? "rgba(37,211,102,0.15)" : "rgba(255,255,255,0.04)",
                      color: !ch.ready ? "#4B5563"
                        : active ? "#25D366" : "#6B7280",
                      border: `1px solid ${!ch.ready ? "rgba(255,255,255,0.05)"
                        : active ? "rgba(37,211,102,0.3)" : "rgba(255,255,255,0.08)"}`,
                    }}
                  />
                );
              })}
            </Box>
          ) : field.type === "select" ? (
            <select
              ref={inputRef}
              defaultValue={field.valueMasked || field.options?.[0] || ""}
              onChange={(e) => { setEdited(true); if (onModeChange) onModeChange(field.key, e.target.value); }}
              style={{
                width: "100%", padding: "8px 10px", borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.1)", backgroundColor: "#1a1d29",
                color: "#fff", fontSize: 13, outline: "none",
              }}
            >
              {(field.options || []).map((o) => <option key={o} value={o}>{OPTION_LABELS[o] || o}</option>)}
            </select>
          ) : (
            <input
              ref={inputRef}
              type={field.isSecret && !showSecret ? "password" : "text"}
              defaultValue={field.isSecret ? "" : (field.valueMasked || "")}
              placeholder={field.configured ? (field.isSecret ? "••••••••" : "") : "Nao configurado"}
              onChange={() => setEdited(true)}
              style={{
                width: "100%", padding: "8px 10px", borderRadius: 8,
                border: `1px solid ${edited ? "rgba(107,72,255,0.4)" : "rgba(255,255,255,0.1)"}`,
                backgroundColor: "#1a1d29", color: "#fff", fontSize: 13, outline: "none",
              }}
            />
          )}
        </Box>
        {field.isSecret && (
          <IconButton
            size="small"
            onClick={async () => {
              if (!showSecret && !edited && field.configured && inputRef.current) {
                try {
                  const { data } = await hubApi.revealSecret(field.key);
                  inputRef.current.value = data.value || "";
                } catch { /* mantem mascarado */ }
              } else if (showSecret && !edited && inputRef.current) {
                inputRef.current.value = "";
              }
              setShowSecret(!showSecret);
            }}
            sx={{ color: "#6B7280" }}
          >
            {showSecret ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
          </IconButton>
        )}
        {field.type !== "channels" && (
          <Chip size="small" label={field.configured ? "OK" : "Pendente"} sx={{
            height: 20, fontSize: 9, fontWeight: 600, minWidth: 55,
            bgcolor: field.configured ? "rgba(37,211,102,0.1)" : "rgba(245,158,11,0.1)",
            color: field.configured ? "#25D366" : "#F59E0B",
          }} />
        )}
        {edited && (
          <Button size="small" onClick={handleSave} disabled={saving}
            sx={{ minWidth: 60, textTransform: "none", fontSize: 11, color: "#6B48FF", fontWeight: 600 }}>
            {saving ? <CircularProgress size={12} /> : "Salvar"}
          </Button>
        )}
      </Box>
    </Box>
  );
}

/*
 * Registro deste HUB no Central (Fase 15, Bloco F).
 *
 * O operador cola a URL do Central e o token hrt_ (emitido no painel do Central, pagina
 * HUBs → Registrar novo HUB). O proprio Local chama o Central, recebe o connectionToken e
 * persiste tudo — nenhum segredo passa pela mao do operador alem do token descartavel.
 */
function RegistroNoCentralCard({ centralStatus, showSnackbar, onRegistered }) {
  const [url, setUrl] = useState("");
  const [token, setToken] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null); // { hubId, instanceId }

  const registrado = !!centralStatus?.lastSuccessfulCentral || !!resultado;

  const registrar = async () => {
    const u = url.trim().replace(/\/+$/, "");
    const t = token.trim();
    if (!/^https?:\/\//i.test(u)) {
      showSnackbar?.("Informe a URL do Central comecando com http:// ou https://", "error");
      return;
    }
    if (!/^hrt_[0-9a-f]{64}$/i.test(t)) {
      showSnackbar?.("Token invalido — o token de registro comeca com hrt_ (emitido no painel do Central).", "error");
      return;
    }
    setEnviando(true);
    try {
      const { data } = await hubApi.registrarNoCentral({ urlDoCentral: u, token: t });
      setResultado(data);
      setToken("");
      showSnackbar?.(`Registrado no Central (hub #${data.hubId}). Heartbeat a cada ${data.heartbeatInterval || 60}s.`);
      onRegistered?.();
    } catch (err) {
      const resp = err.response?.data;
      showSnackbar?.(resp?.message || resp?.error || "Falha ao registrar no Central", "error");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Box sx={{ mb: 2, p: 2, bgcolor: "rgba(37,211,102,0.04)", border: "1px solid rgba(37,211,102,0.15)", borderRadius: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#25D366" }}>
          Registro no HUB Central
        </Typography>
        {registrado && (
          <Chip size="small" label={resultado ? `registrado — hub #${resultado.hubId}` : `conectado: ${centralStatus.lastSuccessfulCentral}`}
            sx={{ height: 18, fontSize: 9, bgcolor: "rgba(37,211,102,0.12)", color: "#25D366" }} />
        )}
      </Box>
      <Typography sx={{ fontSize: 12, color: "#9CA3AF", mb: 1.5, lineHeight: 1.6 }}>
        Peca ao administrador do Central um <b>token de registro</b> (comeca com <code>hrt_</code>,
        uso unico — painel do Central, pagina HUBs → Registrar novo HUB). O registro grava a
        credencial automaticamente; nada precisa ser colado alem do token.
        {registrado ? " Registrar de novo substitui o vinculo atual." : ""}
      </Typography>
      <TextField
        label="URL do HUB Central" fullWidth size="small" sx={{ mb: 1.5 }}
        value={url} onChange={(e) => setUrl(e.target.value)}
        placeholder="https://hub.seudominio.com.br"
        InputLabelProps={{ sx: { color: "#6B7280" } }}
        InputProps={{ sx: { color: "#fff", bgcolor: "#1F2937", borderRadius: 2 } }}
      />
      <TextField
        label="Token de registro (hrt_...)" fullWidth size="small" sx={{ mb: 1.5 }}
        value={token} onChange={(e) => setToken(e.target.value)}
        placeholder="hrt_..."
        InputLabelProps={{ sx: { color: "#6B7280" } }}
        InputProps={{ sx: { color: "#fff", bgcolor: "#1F2937", borderRadius: 2, fontFamily: "monospace" } }}
      />
      <Button
        onClick={registrar}
        disabled={enviando || !url.trim() || !token.trim()}
        variant="contained" size="small"
        sx={{ background: "linear-gradient(135deg, #25D366, #10B981)", textTransform: "none", fontWeight: 700 }}
      >
        {enviando ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Registrar este HUB no Central"}
      </Button>
      {resultado && (
        <Typography sx={{ fontSize: 11, color: "#9CA3AF", mt: 1 }}>
          instanceId: <code>{resultado.instanceId}</code>
        </Typography>
      )}
    </Box>
  );
}

export default function SettingsPage({ showSnackbar }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [centralStatus, setCentralStatus] = useState(null);
  const [restarting, setRestarting] = useState(false);
  const [restartDialogOpen, setRestartDialogOpen] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResults, setTestResults] = useState({});
  // Mode overrides — atualizam visibilidade em tempo real ao mudar selects
  const [modeOverrides, setModeOverrides] = useState({});

  const loadSettings = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true); // silent = recarrega sem desmontar a lista (preserva o scroll)
      const { data: resp } = await hubApi.getSettings();
      setData(resp);
    } catch {
      showSnackbar?.("Erro ao carregar configurações", "error");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // Polling status do CentralClient (failover) — relevante so em HUB Local
  useEffect(() => {
    let timer;
    const fetchStatus = async () => {
      try {
        const { data: st } = await hubApi.getCentralStatus();
        setCentralStatus(st);
      } catch { /* silencioso */ }
    };
    fetchStatus();
    timer = setInterval(fetchStatus, 30000);
    return () => clearInterval(timer);
  }, []);

  const handleSave = async (key, value) => {
    try {
      const { data: result } = await hubApi.updateSetting(key, value);
      showSnackbar?.(
        result.requiresRestart
          ? `${key} salvo. Reinicie o serviço para aplicar.`
          : `${key} salvo e aplicado!`
      );
      loadSettings(true); // silencioso: não joga a página pro topo ao salvar um campo
    } catch (err) {
      showSnackbar?.("Erro ao salvar", "error");
      throw err;
    }
  };

  const handleRestart = async () => {
    setRestarting(true);
    try {
      await hubApi.restartHub();
      showSnackbar?.("Reiniciando servico. Aguarde 10 segundos e recarregue a pagina.", "info");
      setRestartDialogOpen(false);
      // Após 12s, tenta recarregar settings (servico já deve estar de volta)
      setTimeout(() => { loadSettings(); setRestarting(false); }, 12000);
    } catch (err) {
      showSnackbar?.(err?.response?.data?.error || "Erro ao reiniciar", "error");
      setRestarting(false);
    }
  };

  const handleTest = async (service) => {
    setTesting(service);
    try {
      const { data: result } = await hubApi.testService(service);
      setTestResults((prev) => ({ ...prev, [service]: result }));
      const ok = result.status === "ok";
      const msg = ok
        ? `${service}: OK${result.detalhe ? " — " + result.detalhe : result.latencyMs ? " (" + result.latencyMs + "ms)" : ""}`
        : `${service}: ${result.error || result.status}`;
      showSnackbar?.(msg, ok ? "success" : "error");
    } catch {
      setTestResults((prev) => ({ ...prev, [service]: { status: "error" } }));
      showSnackbar?.(`${service}: erro ao testar`, "error");
    }
    setTesting(null);
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: "#6B48FF" }} /></Box>;
  }

  const settings = data?.settings || [];
  const services = data?.services || {};
  // HUB_ROLE vem do servidor (definido no .env, nunca editavel pelo painel).
  // central = servidor administrador OtimizAPP / local = HUB de parceiro/cliente
  const hubRole = (data?.hubRole || "local").toLowerCase();
  const isCentralRole = hubRole === "central";

  // Detectar modo atual (override local > valor salvo no backend)
  const getVal = (key) => modeOverrides[key] || (settings.find((s) => s.key === key)?.valueMasked) || "";
  const currentMode = getVal("HUB_MODE") || "centralized";
  const currentOwnership = getVal("META_APP_OWNERSHIP") || "central";
  const currentMarketplace = getVal("MARKETPLACE_MODE") || "full";
  const isIndependent = currentMode === "independent";

  // Resolver visibilidade por seção
  // HUB CENTRAL: gerencia tudo (Meta + Telegram + Marketplace + Segurança + Serviços) — sem opção de modo
  // HUB LOCAL Centralizado: só Conexão Central + Segurança + Serviços
  // HUB LOCAL Independente: Meta + Telegram + Marketplace + Segurança + Serviços
  // HUB Central: nao tem "modo" (ele E o central) → mostra Meta/Telegram/Marketplace direto
  // HUB Local Centralizado: mostra Conexao Central
  // HUB Local Independente: mostra Meta/Telegram/Marketplace proprios
  const sectionVisible = {
    central_status: isCentralRole,                       // So no HUB Central
    mode: !isCentralRole,                                // Modo so faz sentido no Local
    central: !isCentralRole && !isIndependent,           // Conexao Central so no Local Centralizado
    meta: isCentralRole || isIndependent,                // Central (admin) ou Local Independente
    telegram: isCentralRole || isIndependent,
    marketplace: (isCentralRole || isIndependent) && currentMarketplace !== "disabled",
    email_sistema: isCentralRole || isIndependent,
    security: true,
    services: true,
  };

  // Agrupar por seção (filtrar por visibilidade de seção + campo)
  const grouped = {};
  for (const s of settings) {
    if (!sectionVisible[s.section]) continue;
    // Filtro por campo: visibleWhen casa com o modo atual
    if (s.visibleWhen && s.visibleWhen !== currentMode) continue;
    if (!grouped[s.section]) grouped[s.section] = [];
    grouped[s.section].push(s);
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#fff" }}>Configuracoes</Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
            Gerenciar integrações e serviços do HUB
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Recarregar"><IconButton onClick={loadSettings} sx={{ color: "#6B7280" }}><Refresh /></IconButton></Tooltip>
          {settings.some((s) => s.pendingRestart || s.requiresRestart) && (
            <Button
              variant="contained"
              startIcon={<RestartAlt />}
              onClick={() => setRestartDialogOpen(true)}
              disabled={restarting}
              sx={{
                background: "linear-gradient(135deg, #F59E0B, #FB923C)",
                textTransform: "none", fontWeight: 700, borderRadius: 2, px: 2,
              }}
            >
              Reiniciar servico
            </Button>
          )}
        </Box>
      </Box>

      {/* Alertas */}
      {isCentralRole && (
        <Card sx={{ bgcolor: "rgba(107,72,255,0.06)", border: "1px solid rgba(107,72,255,0.2)", borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <CheckCircle sx={{ fontSize: 18, color: "#6B48FF" }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#6B48FF" }}>
                HUB Central — Administrador OtimizAPP
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: "#9CA3AF", lineHeight: 1.5 }}>
              Voce administra o ecossistema. Configure aqui os Apps Meta, Telegram e Marketplace
              que serao utilizados pelos HUBs Locais conectados em modo centralizado.
            </Typography>
          </CardContent>
        </Card>
      )}
      {!isCentralRole && currentMode === "independent" && (
        <Card sx={{ bgcolor: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Warning sx={{ fontSize: 18, color: "#F59E0B" }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#F59E0B" }}>
                Modo Independente
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: "#9CA3AF", lineHeight: 1.5 }}>
              Voce gerencia seus proprios Apps e configuracoes. Configure Meta, Telegram e Marketplace abaixo.
              Voce assume responsabilidade por permissoes, webhooks e manutencao.
            </Typography>
          </CardContent>
        </Card>
      )}
      {!isCentralRole && currentMode === "centralized" && (
        <Card sx={{ bgcolor: "rgba(37,211,102,0.06)", border: "1px solid rgba(37,211,102,0.15)", borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <CheckCircle sx={{ fontSize: 18, color: "#25D366" }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#25D366" }}>
                Modo Centralizado
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: "#9CA3AF", lineHeight: 1.5 }}>
              Conectado ao ecossistema OtimizAPP Connect. Meta, Telegram e Marketplace sao gerenciados pelo administrador central.
              Informe a URL e o token de acesso na secao "Conexao com HUB Central".
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Alerta de failover ativo */}
      {!isCentralRole && centralStatus?.inFallback && (
        <Card sx={{ bgcolor: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Warning sx={{ fontSize: 18, color: "#F59E0B" }} />
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#F59E0B" }}>
                Em Fallback — Central primario indisponivel
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>
              {centralStatus.lastPrimaryError === "central_inactive"
                ? "O Central principal foi marcado como inativo pelo administrador."
                : `Erro no primario: ${centralStatus.lastPrimaryError || "desconhecido"}`}
              <br />
              Usando agora: <b>{centralStatus.lastSuccessfulCentral}</b>
            </Typography>
          </CardContent>
        </Card>
      )}

      <Box sx={{ columnGap: 2, columnCount: { xs: 1, md: 2 } }}>
        {Object.entries(SECTIONS).map(([sectionKey, section]) => {
          // Filtrar seção por visibilidade
          if (!sectionVisible[sectionKey]) return null;
          const fields = grouped[sectionKey] || [];
          // "central" sempre renderiza no Local Centralizado: o cartao de registro vive nela.
          if (fields.length === 0 && sectionKey !== "services" && sectionKey !== "central") return null;
          const allConfigured = fields.length > 0 && fields.every((f) => f.configured);
          const noneConfigured = fields.length > 0 && fields.every((f) => !f.configured);
          const testResult = testResults[sectionKey];

          return (
            <Box key={sectionKey} sx={{ breakInside: "avoid", mb: 2 }}>
              <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3 }}>
                <CardContent sx={{ p: 2.5 }}>
                  {/* Header */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Typography sx={{ fontSize: 22 }}>{section.icon}</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#fff" }}>{section.label}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        size="small"
                        label={allConfigured ? "Configurado" : noneConfigured ? "Pendente" : "Parcial"}
                        sx={{
                          fontSize: 10, height: 20,
                          bgcolor: allConfigured ? "rgba(37,211,102,0.1)" : noneConfigured ? "rgba(239,68,68,0.1)" : "rgba(245,158,11,0.1)",
                          color: allConfigured ? "#25D366" : noneConfigured ? "#EF4444" : "#F59E0B",
                        }}
                      />
                      {(sectionKey === "marketplace" || sectionKey === "services" || sectionKey === "email_sistema") && (
                        <Button
                          size="small"
                          onClick={() => handleTest(sectionKey === "services" ? "redis" : sectionKey)}
                          disabled={testing === sectionKey}
                          sx={{ minWidth: 50, textTransform: "none", fontSize: 10, color: "#6B48FF" }}
                        >
                          {testing === sectionKey ? <CircularProgress size={12} /> : "Testar"}
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {section.help && (
                    <Typography sx={{ fontSize: 13, color: "#9CA3AF", mb: 1.5, lineHeight: 1.5 }}>
                      {section.help}
                    </Typography>
                  )}

                  {/* Passo a passo + botão de ação */}
                  {section.steps && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: "rgba(107,72,255,0.04)", borderRadius: 2, border: "1px solid rgba(107,72,255,0.1)" }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6B48FF", mb: 0.5 }}>Como configurar:</Typography>
                      {section.steps.map((step, i) => (
                        <Typography key={i} sx={{ fontSize: 13, color: "#D1D5DB", lineHeight: 1.8 }}>
                          {step}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {section.actionUrl && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => window.open(section.actionUrl, "_blank")}
                      startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        mb: 2, textTransform: "none", fontSize: 11, fontWeight: 600,
                        color: section.color || "#6B48FF",
                        borderColor: (section.color || "#6B48FF") + "40",
                        "&:hover": { borderColor: section.color || "#6B48FF", bgcolor: (section.color || "#6B48FF") + "10" },
                      }}
                    >
                      {section.actionLabel || "Abrir"}
                    </Button>
                  )}

                  {/* Service status */}
                  {sectionKey === "services" && (
                    <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
                      <Chip size="small"
                        icon={services.redis === "connected" ? <CheckCircle sx={{ fontSize: 12, color: "#25D366 !important" }} /> : <Error sx={{ fontSize: 12, color: "#EF4444 !important" }} />}
                        label={`Redis: ${services.redis}`}
                        sx={{ fontSize: 10, height: 22, bgcolor: services.redis === "connected" ? "rgba(37,211,102,0.1)" : "rgba(239,68,68,0.1)", color: services.redis === "connected" ? "#25D366" : "#EF4444" }}
                      />
                      <Chip size="small"
                        icon={<CheckCircle sx={{ fontSize: 12, color: "#25D366 !important" }} />}
                        label="HUB: running"
                        sx={{ fontSize: 10, height: 22, bgcolor: "rgba(37,211,102,0.1)", color: "#25D366" }}
                      />
                    </Box>
                  )}

                  {/* Registro no Central (so no HUB Local em modo centralizado) */}
                  {sectionKey === "central" && (
                    <RegistroNoCentralCard
                      centralStatus={centralStatus}
                      showSnackbar={showSnackbar}
                      onRegistered={loadSettings}
                    />
                  )}

                  {/* Fields */}
                  {fields.map((field) => (
                    <SettingField
                      key={field.key}
                      field={field}
                      onSave={handleSave}
                      onModeChange={field.section === "mode" ? (k, v) => setModeOverrides((prev) => ({ ...prev, [k]: v })) : undefined}
                    />
                  ))}
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>

      {/* Dialog de confirmacao de restart */}
      <Dialog open={restartDialogOpen} onClose={() => !restarting && setRestartDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: "#1a1d29", color: "#fff", borderRadius: 3 } }}>
        <DialogTitle sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          Reiniciar servico?
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontSize: 13, color: "#D1D5DB", mb: 2 }}>
            O servico sera reiniciado para aplicar configuracoes que exigem reinicio.
          </Typography>
          <Box sx={{ p: 2, bgcolor: "rgba(245,158,11,0.08)", borderRadius: 2, border: "1px solid rgba(245,158,11,0.2)" }}>
            <Typography sx={{ fontSize: 12, color: "#F59E0B", fontWeight: 600, mb: 0.5 }}>
              <Warning sx={{ fontSize: 14, mr: 0.5, verticalAlign: "text-bottom" }} />
              Aviso
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#9CA3AF", lineHeight: 1.5 }}>
              Conexoes ativas podem cair temporariamente (5-15 segundos). Webhooks recebidos durante o reinicio podem ser perdidos se nao houver retry. Cada conexao tenta reconectar automaticamente apos o servico voltar.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <Button onClick={() => setRestartDialogOpen(false)} disabled={restarting} sx={{ color: "#9CA3AF", textTransform: "none" }}>
            Cancelar
          </Button>
          <Button onClick={handleRestart} disabled={restarting} variant="contained"
            sx={{ background: "linear-gradient(135deg, #F59E0B, #FB923C)", textTransform: "none", fontWeight: 700 }}>
            {restarting ? <CircularProgress size={16} /> : "Reiniciar agora"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
